import { useSnackbar } from '@components/snackbar';
import { SearchIcon, TimerIcon } from '@sketchnote/icons';
import { LoaderSm } from '@sketchnote/shared-components';
import { PlayCircle, StopCircle } from '@styled-icons/material';
import cn from 'classnames';
import React, { useEffect } from 'react';
import AsyncSelect from 'react-select/async';
import { API } from 'src/api';
import { TimerTracker } from 'src/features/tasks/components/TimeTrackerDetails';
import { getFormattedValue } from 'src/utils/getFormattedValue';
import { truncateString } from 'src/utils/truncateString';

import { Timer, useTimerWidgetStore } from './useTimerWidgetStore';

const TaskTimer = ({ timer }: { timer: Timer }) => {
  const { startTimer, stopTimer } = useTimerWidgetStore((s) => ({
    startTimer: s.startTimer,
    stopTimer: s.stopTimer,
  }));
  const ICON_SIZE = 24;
  const [openSnackbar] = useSnackbar();

  const onStartTimer = () => {
    startTimer(timer.task._id);
  };

  const onStopTimer = () => {
    stopTimer(timer.task._id, timer.startTime, timer.billable);
    openSnackbar({
      text: `Timer stopped for ${truncateString(timer.task_title, 20)}`,
      snackType: 'standard',
      position: 'bottom-center',
    });
  };

  return (
    <div
      key={timer._id}
      className="flex items-center justify-between py-12 px-20 hover:bg-gray-100 transition-colors duration-300 overflow-hidden"
    >
      <div className=" flex-grow overflow-auto pr-12">
        <div className="text-xs text-gray-500 title">{timer.board_title || ''}</div>
        <div className="text-sm text-gray-700 title">{timer.task_title || ''}</div>
      </div>
      <div className="flex border rounded-full border-gray-200 p-2 pr-4 shadow-xs justify-between items-center">
        {/* <PauseCircle size={ICON_SIZE} className="text-blue-400 mr-8" /> */}
        {!timer.isRunning ? (
          <button
            onClick={onStartTimer}
            disabled={timer.isRunning}
            className="text-blue-400 hover:text-blue-600 active:text-blue-800"
          >
            <PlayCircle size={ICON_SIZE} />
          </button>
        ) : (
          <button
            onClick={onStopTimer}
            disabled={!timer.isRunning}
            className={cn('hover:text-red-600 active:text-red-500', {
              ' text-red-200': !timer.isRunning,
              'text-red-400': timer.isRunning,
            })}
          >
            <StopCircle size={ICON_SIZE} />
          </button>
        )}
        <TimerTracker
          className=" text-gray-600 min-w-75 font-medium text-xs text-center"
          startTime={timer?.startTime}
          stopTime={timer?.stopTime}
          isRunning={timer?.isRunning}
          timeTrackedTaskId={timer?.task._id}
        />
      </div>
    </div>
  );
};

export const RunningTimers = () => {
  const { timers, fetchTimers, isLoading, addTimer } = useTimerWidgetStore((s) => ({
    timers: s.timers,
    fetchTimers: s.fetchTimers,
    isLoading: s.isLoading,
    addTimer: s.addTimer,
  }));

  useEffect(() => {
    fetchTimers();
  }, []);

  const onSelectTask = (selectedOption: any) => {
    if (!selectedOption) {
      return;
    }
    const { data } = selectedOption;
    const now = new Date().toISOString();
    const taskTimer: Timer = {
      billable: false,
      board: data.board,
      board_title: '',
      createdAt: data.createdAt,
      file: data.fileRef || '',
      filename: '',
      isRunning: false,
      member: '',
      startDate: now,
      startTime: now,
      status: data.status || '',
      task: data,
      task_title: getFormattedValue(data.name),
      task_type: 'task',
      updatedAt: data.updatedAt,
      _id: data._id,
    };
    addTimer(taskTimer);
  };

  const loadOptions = async (inputValue: string) => {
    const response = await API.get(`/board/task/search?search=${inputValue}`);
    const existingTimerTaskIds = timers.map((timer: any) => timer.task._id);
    const tasks = response.data.data;
    return tasks
      .filter((task: any) => !existingTimerTaskIds.includes(task._id))
      .map((task: any) => ({
        value: task._id,
        label: getFormattedValue(task.name),
        data: task,
      }));
  };

  return (
    <div className="h-screen flex flex-col">
      <header className="w-full p-12 text-sm font-medium bg-gray-100 border-b border-gray-200">
        Active Timers
      </header>
      <section className="relative flex-grow">
        {isLoading && (
          <div className="absolute top-0 right-0 w-full h-full bg-white opacity-70 p-4 z-100 flex justify-center items-center overflow-hidden">
            <LoaderSm className="loadSize" />
          </div>
        )}
        <section
          className="py-12 px-16 sticky top-0 "
          style={{
            backdropFilter: 'blur(3px)',
            WebkitBackdropFilter: 'blur(3px)',
          }}
        >
          <AsyncSelect
            styles={{
              control: (provided) => ({
                ...provided,
                padding: '4px',
                borderColor: '#E2E4E9',
                borderRadius: '32px',
                boxShadow: '0px 0px 2px rgba(0, 0, 0, 0.1)',
                fontSize: '14px',
              }),
            }}
            cacheOptions
            noOptionsMessage={({ inputValue }) => {
              if (inputValue.trim() === '') {
                return null;
              }

              return 'No options found';
            }}
            isClearable
            onChange={onSelectTask}
            loadOptions={loadOptions}
            placeholder="Search tasks"
            components={{
              IndicatorSeparator: () => null,
              DropdownIndicator: () => <SearchIcon className=" text-gray-400 mr-10" />,
            }}
          />
        </section>
        <section className="flex-grow pb-64">
          {timers.length === 0 && (
            <div className="text-gray-500 text-center flex text-sm p-20 w-full rounded-lg justify-center items-center">
              <TimerIcon size={24} className="text-gray-500 mr-2" />
              <span>No active timers</span>
            </div>
          )}
          {timers.map((timer: any) => (
            <TaskTimer key={timer._id} timer={timer} />
          ))}
        </section>
      </section>
    </div>
  );
};
